/*
Enhanced Messages component with bulk selection and discard functionality
Based on original Messages.tsx but adds bulk operations for "Otros" messages
Author: Implementation for bulk discard feature  
Date: 2025-01-15
*/
import * as React from 'react'
import DataTable, { TableColumn } from 'react-data-table-component'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { fechaIsoToFull } from '../utils/utils'
import useMessageFetcher from '../hooks/useMessageFetcher'
import { useBulkMessageOperations } from '../hooks/useBulkMessageOperations'
import { useAuth } from '../hooks/useAuth'
import { AppPath } from '../enums/AppPath'
import Message from '../interfaces/Message'
import { MessageState } from '../enums/DocStates'
import useFilteredData from '../hooks/useFilteredData'

import { useToast } from './ToastProvider'
import { BulkMessageActions } from './BulkMessageActions'
import CardOneSearch from './CardOneSearch'
import { ButtonMenuSet } from './Buttons'

const dateSort = (rowA: Message, rowB: Message) => {
  const today = new Date()
  const a = rowA.createdAt ?? today
  const b = rowB.createdAt ?? today

  if (a > b) {
    return 1
  }

  if (b > a) {
    return -1
  }

  return 0
}

const MessagesWithBulkActions: React.FC = () => {
  const [clickedIndex, setClickedIndex] = useState<number>(MessageState.Consultas)
  const [newUnreadCount, setNewUnreadCount] = useState<number>(0)
  const { fetchMessages, errorMessage } = useMessageFetcher()
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [array, setArray] = useState<Message[]>([])
  const { userData } = useAuth()
  const { showToast } = useToast()

  // Bulk operations hook
  const {
    selectedCount,
    isLoading: isBulkLoading,
    error: bulkError,
    toggleMessageSelection,
    selectAllMessages,
    clearSelection,
    isMessageSelected,
    bulkDiscardMessages,
  } = useBulkMessageOperations()

  const fetchAsync = async (orgId: string, status: number) => {
    const messages: Message[] = await fetchMessages(orgId, status)

    if (!messages) {
      return
    }

    setArray(messages)
    // Clear selection when changing tabs or refreshing
    clearSelection()

    // Calculate new unread messages since login
    const loginTimestamp = Number(localStorage.getItem('loginTimestamp'))
    const newUnreadMessages = messages.filter(
      (msg) => !msg.readAt && new Date(msg.createdAt).getTime() > loginTimestamp
    )

    setNewUnreadCount(newUnreadMessages.length)
  }

  // Store login timestamp in localStorage if not already set
  useEffect(() => {
    if (!localStorage.getItem('loginTimestamp')) {
      localStorage.setItem('loginTimestamp', Date.now().toString())
    }
  }, [])

  useEffect(() => {
    if (!userData) return
    void fetchAsync(userData?.organization?._id ?? '', clickedIndex)
  }, [clickedIndex])

  // Auto-refresh: poll for new messages every 30 seconds
  useEffect(() => {
    if (!userData) return
    const interval = setInterval(() => {
      void fetchAsync(userData.organization?._id ?? '', clickedIndex)
    }, 30000) // 30 seconds

    return () => clearInterval(interval)
  }, [clickedIndex])

  // Handle bulk error display
  useEffect(() => {
    if (bulkError) {
      showToast(bulkError.message, 'error')
    }
  }, [bulkError, showToast])

  const navigate = useNavigate()

  const onRowClickedHandler = (row: Message): void => {
    // Don't navigate if we're in selection mode for Otros
    if (clickedIndex === MessageState.Otros && selectedCount > 0) {
      toggleMessageSelection(row._id)

      return
    }

    const url = `/${AppPath.Messages}/view/${row._id}`
    navigate(url)
  }

  // Para la busqueda
  const filtered = useFilteredData({
    array,
    filterFields: [
      (item) => item.assignedTo_user ?? '',
      (item) => item.vehicle ?? '',
      (item) => item.contact?.name ?? '',
      (item) => item.contact?.email ?? '',
    ],
    searchTerm,
    allowFilter: true,
  })

  const onClearClick = () => {
    // Clear search and selection
    clearSelection()
  }

  // Handle bulk discard
  const handleBulkDiscard = async () => {
    const result = await bulkDiscardMessages()

    if (result) {
      showToast(`Se descartaron ${result.discarded} de ${result.requested} mensajes`, 'discard')

      // Refresh the current tab
      if (userData) {
        await fetchAsync(userData.organization?._id ?? '', clickedIndex)
      }
    }
  }

  // Handle select all
  const handleSelectAll = () => {
    const eligibleMessages = filtered.filter((msg) => !msg.discardedAt && !msg.deal)
    selectAllMessages(eligibleMessages.map((msg) => msg._id))
  }

  // Enhanced columns with selection checkbox for Otros tab
  const getColumns = (): TableColumn<Message>[] => {
    const baseColumns: TableColumn<Message>[] = [
      {
        name: 'Asignado',
        selector: (row) => row.assignedTo_user ?? '',
        sortable: true,
        wrap: true,
        grow: 0,
      },
      {
        name: 'Recibido',
        selector: (row) => fechaIsoToFull(row.createdAt),
        sortable: true,
        sortFunction: dateSort,
        grow: 1,
      },
      {
        name: 'Comercio',
        selector: (row) => row.external,
        sortable: true,
        wrap: true,
        grow: 0,
      },
      {
        name: 'Vehículo/Asunto',
        sortable: true,
        cell: (row) => row.vehicle,
        grow: 1,
      },
      {
        name: 'Remitente',
        selector: (row) => row.contact?.name ?? '',
        sortable: true,
        wrap: true,
        grow: 1,
      },
      {
        name: 'Teléfono',
        selector: (row) => row.contact?.phone ?? '',
        sortable: true,
        grow: 1,
      },
      {
        name: 'Email',
        selector: (row) => row.contact?.email ?? '',
        sortable: true,
        grow: 1,
      },
      {
        name: 'Anuncio',
        cell: (row) => (
          <a href={row.adUrl} target="_blank" rel="noopener noreferrer">
            Ver
          </a>
        ),
        grow: 0,
      },
    ]

    // Add selection column for Otros tab
    if (clickedIndex === MessageState.Otros) {
      return [
        {
          name: '',
          cell: (row) => (
            <input
              type="checkbox"
              checked={isMessageSelected(row._id)}
              onChange={() => toggleMessageSelection(row._id)}
              disabled={!!row.discardedAt || !!row.deal} // Disable for already processed/discarded
              onClick={(e) => e.stopPropagation()} // Prevent row click
            />
          ),
          grow: 0,
          width: '50px',
        },
        ...baseColumns,
      ]
    }

    return baseColumns
  }

  const buttonCaptions = ['Consultas', 'Otros', 'Procesados', 'Descartados']
  const isOtrosTab = clickedIndex === MessageState.Otros
  const eligibleForSelection = filtered.filter((msg) => !msg.discardedAt && !msg.deal)

  return (
    <div>
      {newUnreadCount > 0 && (
        <div
          style={{
            background: '#E6F4FF',
            color: '#005A9E',
            padding: '8px',
            borderRadius: '6px',
            marginBottom: '12px',
            fontWeight: 'bold',
            textAlign: 'center',
          }}
        >
          Tienes {newUnreadCount} mensaje{newUnreadCount > 1 ? 's' : ''} nuevo{newUnreadCount > 1 ? 's' : ''} sin leer
          desde que ingresaste.
        </div>
      )}

      <CardOneSearch
        headerText={'Gestionar '}
        docuType={AppPath.Messages}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        onClearClick={onClearClick}
      />

      <div className="mt-2 p-2 mx-auto ">
        <ButtonMenuSet
          captions={buttonCaptions}
          clickedIndex={clickedIndex}
          handleButtonClick={(n) => setClickedIndex(n)}
        />

        {/* Bulk Actions Toolbar - only show for Otros tab */}
        {isOtrosTab && (
          <div className="mt-4">
            <BulkMessageActions
              selectedCount={selectedCount}
              totalCount={eligibleForSelection.length}
              onSelectAll={handleSelectAll}
              onClearSelection={clearSelection}
              onBulkDiscard={handleBulkDiscard}
              isLoading={isBulkLoading}
              disabled={eligibleForSelection.length === 0}
            />
          </div>
        )}

        <div className="mt-4">
          <DataTable
            columns={getColumns()}
            data={filtered}
            highlightOnHover
            pointerOnHover
            dense
            onRowClicked={onRowClickedHandler}
            conditionalRowStyles={[
              {
                when: (row) => !row.readAt,
                style: {
                  fontWeight: 'normal',
                  backgroundColor: '#E6F4FF',
                },
              },
              {
                when: (row) => !!row.readAt,
                style: {
                  fontWeight: 'normal',
                  backgroundColor: '#FFFFFF',
                },
              },
              {
                when: (row) => isOtrosTab && isMessageSelected(row._id),
                style: {
                  backgroundColor: '#FFF2E8',
                  border: '1px solid #FF6B35',
                },
              },
            ]}
          />
        </div>
        <div>{errorMessage?.message}</div>
      </div>
    </div>
  )
}

export default MessagesWithBulkActions
